import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Skeleton, Tabs, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { memberState } from "../../../../state/atoms";
import DataKitApi from "../../../../api/DataKitApi";
import {
  DataKitDto,
  DataKitGroupDto,
  DataKitItemDto,
  DataKitMenuDto,
  DataKitPublishDiffDto,
  MenuUsageDto,
} from "../../../../domain/DataKitDto";
import { env } from "../../../../env/env";
import { DataKitItemsTab } from "./DataKitItemsTab";
import { DataKitItemDrawer, blankDataKitItem } from "./DataKitItemDrawer";
import { DataKitImportModal } from "./DataKitImportModal";
import { DataKitPublishBar } from "./DataKitPublishBar";
import { DataKitGroupsTab } from "./DataKitGroupsTab";
import { DataKitMenusTab } from "./DataKitMenusTab";
import { hasDataKitBeta } from "./dataKitAccess";
import "./DataKitDetailPage.scss";

export function DataKitDetailPage() {
  const { kitId } = useParams();
  const member = useRecoilValue(memberState);
  const canWrite = !!member && (member.owner || member.role === "Admin");

  const [kit, setKit] = useState<DataKitDto | null>(null);
  const [items, setItems] = useState<DataKitItemDto[]>([]);
  const [groups, setGroups] = useState<DataKitGroupDto[]>([]);
  const [menus, setMenus] = useState<MenuUsageDto[]>([]);
  const [accountMenus, setAccountMenus] = useState<DataKitMenuDto[]>([]);
  const [diff, setDiff] = useState<DataKitPublishDiffDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [editingItem, setEditingItem] = useState<DataKitItemDto | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const loadKit = useCallback(() => {
    if (!kitId) return Promise.resolve();
    return DataKitApi.getKit(kitId)
      .then((res) => setKit(res.data))
      .catch(() => message.error("Could not load this data kit"));
  }, [kitId]);

  const loadItems = useCallback(
    (includeArchived: boolean) => {
      if (!kitId) return Promise.resolve();
      return DataKitApi.listItems(kitId, includeArchived)
        .then((res) => setItems(res.data || []))
        .catch(() => message.error("Could not load items"));
    },
    [kitId]
  );

  const loadGroups = useCallback(() => {
    if (!kitId) return Promise.resolve();
    return DataKitApi.listGroups(kitId)
      .then((res) => setGroups(res.data || []))
      .catch(() => undefined);
  }, [kitId]);

  const loadMenus = useCallback(() => {
    if (!kitId) return Promise.resolve();
    return DataKitApi.listMenuUsage(kitId)
      .then((res) => setMenus(res.data || []))
      .catch(() => undefined);
  }, [kitId]);

  const loadAccountMenus = useCallback(
    () =>
      DataKitApi.listMenus()
        .then((res) => setAccountMenus(res.data || []))
        .catch(() => undefined),
    []
  );

  const loadDiff = useCallback(() => {
    if (!kitId) return Promise.resolve();
    return DataKitApi.getPublishDiff(kitId)
      .then((res) => setDiff(res.data))
      .catch(() => undefined);
  }, [kitId]);

  useEffect(() => {
    if (!kitId) return;
    setLoading(true);
    Promise.all([loadKit(), loadItems(true), loadGroups(), loadMenus(), loadAccountMenus(), loadDiff()]).finally(() =>
      setLoading(false)
    );
  }, [kitId, loadKit, loadItems, loadGroups, loadMenus, loadAccountMenus, loadDiff]);

  const reloadAll = useCallback(() => {
    loadKit();
    loadItems(true);
    loadGroups();
    loadMenus();
    loadAccountMenus();
    loadDiff();
  }, [loadKit, loadItems, loadGroups, loadMenus, loadAccountMenus, loadDiff]);

  const activeItems = useMemo(() => items.filter((i) => !i.archived), [items]);
  const archivedItems = useMemo(() => items.filter((i) => i.archived), [items]);

  if (!hasDataKitBeta(member)) return null;

  if (loading && !kit) {
    return (
      <div className="data-kit-detail-page">
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (!kit) return null;

  const itemCount = activeItems.length;
  const locationLabel = `${kit.locationCount} location${kit.locationCount === 1 ? "" : "s"}`;

  return (
    <div className="data-kit-detail-page">
      <nav className="data-kit-breadcrumb">
        <Link to={`${env.dashboardPath}/account/data-kits`}>Data Kits</Link>
        <span className="data-kit-breadcrumb-sep">›</span>
        <span className="data-kit-breadcrumb-current">{kit.name || "Untitled"}</span>
      </nav>

      {diff && (
        <DataKitPublishBar kit={kit} diff={diff} canWrite={canWrite} onDone={reloadAll} />
      )}

      <header className="data-kit-detail-header">
        <div className="data-kit-detail-header-text">
          <h1>{kit.name || "Untitled"}</h1>
          <p className="data-kit-detail-meta">
            {itemCount} item{itemCount === 1 ? "" : "s"} • {locationLabel}
          </p>
        </div>
        {canWrite && (
          <div className="data-kit-detail-actions">
            <Button className="data-kit-import-btn" onClick={() => setImportOpen(true)}>
              Import
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="data-kit-add-item-btn"
              onClick={() => setEditingItem(blankDataKitItem())}
            >
              Add Item
            </Button>
          </div>
        )}
      </header>

      <Tabs
        className="data-kit-detail-tabs"
        items={[
          {
            key: "items",
            label: `Items • ${itemCount}`,
            children: (
              <DataKitItemsTab
                kit={kit}
                items={showArchived ? archivedItems : activeItems}
                groups={groups}
                archivedCount={archivedItems.length}
                showArchived={showArchived}
                onToggleArchived={() => setShowArchived((v) => !v)}
                canWrite={canWrite}
                onChanged={reloadAll}
                onEditItem={setEditingItem}
              />
            ),
          },
          {
            key: "groups",
            label: `Groups • ${groups.length}`,
            children: (
              <DataKitGroupsTab
                kit={kit}
                groups={groups}
                      canWrite={canWrite}
                onChanged={reloadAll}
              />
            ),
          },
          {
            key: "menus",
            label: `Menus • ${accountMenus.length}`,
            children: (
              <DataKitMenusTab
                menus={accountMenus}
                usage={menus}
                canWrite={canWrite}
                onChanged={reloadAll}
              />
            ),
          },
        ]}
      />

      {importOpen && (
        <DataKitImportModal
          kit={kit}
          onClose={() => setImportOpen(false)}
          onImported={() => {
            setImportOpen(false);
            reloadAll();
          }}
        />
      )}

      {editingItem && (
        <DataKitItemDrawer
          kit={kit}
          item={editingItem}
          groups={groups}
          menus={accountMenus}
          canWrite={canWrite}
          onStructureChanged={loadGroups}
          onClose={() => setEditingItem(null)}
          onSaved={() => {
            setEditingItem(null);
            reloadAll();
          }}
        />
      )}
    </div>
  );
}
